import { supabase } from '../lib/supabase';

export type SubscriptionIntervalUnit = 'day' | 'week' | 'month' | 'year';
export type SubscriptionTargetType = 'user' | 'company';

export type SubscriptionBenefitKey =
  | 'max_product_images'
  | 'max_products'
  | 'max_jobs'
  | 'featured_products'
  | 'featured_jobs'
  | 'boost_credits_monthly'
  | 'visibility_days'
  | 'priority_support'
  | 'advanced_analytics';

export type SubscriptionBenefitValue = number | boolean;

export interface SubscriptionBenefit {
  key: SubscriptionBenefitKey;
  value: SubscriptionBenefitValue;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string | null;
  currency: string;
  price: number;
  interval_unit: SubscriptionIntervalUnit;
  interval_count: number;
  benefits: SubscriptionBenefit[];
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface PlatformSubscription {
  id: string;
  plan_id: string;
  target_type: SubscriptionTargetType;
  target_id: string;
  status: 'active' | 'cancelled' | 'expired' | 'paused';
  start_at: string | null;
  end_at: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export class PlatformSubscriptionsService {
  static async listPlans(): Promise<SubscriptionPlan[]> {
    const { data, error } = await supabase
      .from('platform_subscription_plans')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []) as SubscriptionPlan[];
  }

  static async upsertPlan(plan: SubscriptionPlan): Promise<SubscriptionPlan> {
    const payload: any = {
      name: String(plan.name || '').trim(),
      description: plan.description ?? null,
      currency: String(plan.currency || 'EUR').toUpperCase(),
      price: Number(plan.price || 0),
      interval_unit: plan.interval_unit,
      interval_count: Math.max(1, Number(plan.interval_count || 1)),
      benefits: Array.isArray(plan.benefits) ? plan.benefits : [],
      is_active: !!plan.is_active,
    };

    const isUpdate = !!String(plan.id || '').trim();

    const q = supabase
      .from('platform_subscription_plans')
      .upsert(isUpdate ? { id: plan.id, ...payload } : payload)
      .select('*')
      .single();

    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return data as SubscriptionPlan;
  }

  static async assignSubscription(input: {
    plan_id: string;
    target_type: SubscriptionTargetType;
    target_id: string;
    start_at: string | null;
    end_at: string | null;
  }): Promise<PlatformSubscription> {
    const payload: any = {
      plan_id: input.plan_id,
      target_type: input.target_type,
      target_id: input.target_id,
      status: 'active',
      start_at: input.start_at,
      end_at: input.end_at,
      metadata: null,
    };

    const { data, error } = await supabase
      .from('platform_subscriptions')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw new Error(error.message);
    return data as PlatformSubscription;
  }

  static async listSubscriptions(params?: { limit?: number }): Promise<PlatformSubscription[]> {
    const limit = Math.min(200, Math.max(1, Number(params?.limit || 50)));
    const { data, error } = await supabase
      .from('platform_subscriptions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw new Error(error.message);
    return (data || []) as PlatformSubscription[];
  }
}
